const VERSION = 1;

type Fetch = typeof fetch;

interface Request {
  info: RequestInfo;
  init: RequestInit | undefined;
}

interface RequestProcessor {
  /**
   * Removes the GraphQL query argument from the request
   */
  removeQuery(request: Request): Request;

  /**
   * Adds the GraphQL request query hash to the request
   */
  addHash(request: Request, queryCode?: string): Promise<Request>;
}

interface GraphQLResponse {
  errors?: {
    message: string;
    extensions?: {
      code?: string;
    };
  }[];
}

/**
 * Creates a fetch implementation that sends GraphQL persisted query requests.
 */
export const createPersistedQueryFetch =
  (fetchImpl: Fetch, queryCode: string): Fetch =>
  async (info, init) => {
    const request = { info, init };
    const requestCopy = JSON.parse(JSON.stringify(request));
    const processor = getRequestProcessor(requestCopy as any, 'GET');

    const requestWithQueryHash = await processor.addHash(
      requestCopy as any,
      queryCode
    );
    const requestWithoutQuery = processor.removeQuery(requestWithQueryHash);

    // send a request without the query
    const res = await fetchImpl(
      requestWithoutQuery.info,
      requestWithoutQuery.init
    );
    const body = await res.json();
    // if the query was not found in the server,
    // send another request with the query
    if (isPersistedQueryNotFoundError(body)) {
      // Deep copy the original request object for the POST request
      const postRequest = JSON.parse(JSON.stringify(request));
      postRequest.info = (request.info as string).split('?')[0];
      postRequest.info = postRequest.info.replace('apq', 'route');
      postRequest.init.method = 'POST'; // Modify the method to POST

      const parsedBody = JSON.parse(request.init?.body as string);
      const query =
        queryCode && parsedBody.query.replace('query', `query ${queryCode}`);
      const variables = parsedBody.variables;

      postRequest.init.body = JSON.stringify({ query, variables }); // Assuming init.body contains the query

      const postRequestProcess = getRequestProcessor(postRequest, 'POST');
      const postRequestWithQueryHash = await postRequestProcess.addHash(
        postRequest as any,
        queryCode
      );
      return fetchImpl(
        postRequestWithQueryHash.info,
        postRequestWithQueryHash.init
      );
    }
    return new Response(JSON.stringify(body), {
      status: res.status,
      statusText: res.statusText,
      headers: res.headers
    });
  };

/**
 * Manipulates a fetch request, implemented per HTTP method type.
 */

function getRequestProcessor(request: Request, meth: string) {
  const method = (meth ?? 'GET').toUpperCase();
  const requestProcessor = requestProcessorByMethod[method];

  if (!requestProcessor) {
    throw new Error(`Unsupported request method: ${method}`);
  }

  return requestProcessor;
}

const requestProcessorByMethod: Record<string, RequestProcessor> = {
  GET: {
    removeQuery: (request) => {
      const [url, params] = splitUrlAndSearchParams(
        getRequestInfoUrl(request.info)
      );
      params.delete('query');
      return {
        ...request,
        info: requestInfoWithUpdatedUrl(
          request.info,
          `${url}?${params.toString()}`
        ),
        init: {
          ...request.init,
          method: 'GET',
          mode: 'cors',
          body: undefined // Remove body for GET requests
        }
      };
    },
    addHash: async (request, queryCode) => {
      const [url, params] = splitUrlAndSearchParams(
        getRequestInfoUrl(request.info)
      );

      let query = params.get('query');
      query =
        (queryCode && query?.replace('query', `query ${queryCode}`)) || '';
      if (!query) {
        throw new Error('GET request must contain a query parameter');
      }

      const hash = await sha256(query);

      params.append(
        'extensions',
        JSON.stringify({
          persistedQuery: {
            version: VERSION,
            sha256Hash: hash
          }
        })
      );

      return {
        ...request,
        info: requestInfoWithUpdatedUrl(
          request.info,
          `${url}?${params.toString()}`
        )
      };
    }
  },
  POST: {
    removeQuery: (request) => {
      if (typeof request.init?.body !== 'string') {
        throw new Error('POST request must contain a body');
      }

      const body = JSON.parse(request.init.body);
      const { query, ...bodyWithoutQuery } = body;

      return {
        ...request,
        init: {
          ...request.init,
          body: JSON.stringify(bodyWithoutQuery)
        }
      };
    },
    addHash: async (request, queryCode) => {
      if (typeof request.init?.body !== 'string') {
        throw new Error('POST request must contain a body');
      }

      const body = JSON.parse(request.init.body);

      if (typeof body.query !== 'string') {
        throw new Error('POST request body must contain a query');
      }

      const hash = await sha256(body.query);
      return {
        ...request,
        init: {
          ...request.init,
          body: JSON.stringify({
            ...body,
            extensions: {
              persistedQuery: {
                version: VERSION,
                sha256Hash: hash
              }
            },
            operationName: queryCode
          })
        }
      };
    }
  }
};

function requestInfoWithUpdatedUrl(
  info: RequestInfo,
  url: string
): RequestInfo {
  return typeof info === 'string'
    ? url
    : {
        ...info,
        url
      };
}

function getRequestInfoUrl(info: RequestInfo) {
  return typeof info === 'string' ? info : info.url;
}

function splitUrlAndSearchParams(
  url: string
): [urlWithoutSearchParams: string, params: URLSearchParams] {
  const startOfSearchParams = url.indexOf('?');

  return startOfSearchParams === -1
    ? [url, new URLSearchParams()]
    : [
        url.slice(0, startOfSearchParams),
        new URLSearchParams(url.slice(startOfSearchParams))
      ];
}

/**
 * Copied from https://newbedev.com/javascript-sha256-hash-js-code-example
 */
async function sha256(message: string) {
  // encode as UTF-8
  const msgBuffer = new TextEncoder().encode(message);

  // hash the message
  // eslint-disable-next-line no-restricted-globals
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);

  // convert ArrayBuffer to Array
  const hashArray = Array.from(new Uint8Array(hashBuffer));

  // convert bytes to hex string
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

  return hashHex;
}

function isPersistedQueryNotFoundError(resBody: GraphQLResponse) {
  return (
    resBody.errors &&
    resBody.errors.length > 0 &&
    resBody.errors[0]?.message === 'PersistedQueryNotFound'
  );
}
